import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Image, Camera, Star, Eye } from 'lucide-react';
import { customerAPI } from '../../services/api';

const ServiceGalleryTab = () => {
  const [myPhotos, setMyPhotos] = useState([]);
  const [showcasePhotos, setShowcasePhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const res = await customerAPI.getGallery(30);
      setMyPhotos(res.data?.my_photos || []);
      setShowcasePhotos(res.data?.showcase_photos || []);
    } catch (error) {
      console.error('Failed to fetch gallery:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="card p-12 text-center">
        <div className="animate-spin w-8 h-8 border-2 border-[var(--primary)] border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-[var(--text-muted)]">Loading gallery...</p>
      </div>
    );
  }

  const PhotoGrid = ({ photos, title, emptyMessage }) => (
    <div className="mb-8">
      <h3 className="text-lg font-bold mb-4">{title}</h3>
      {photos.length === 0 ? (
        <div className="card p-8 text-center bg-[var(--accent)]">
          <Camera className="w-12 h-12 text-[var(--text-muted)] mx-auto mb-3" />
          <p className="text-[var(--text-muted)]">{emptyMessage}</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {photos.map((photo) => (
            <motion.div
              key={photo.id}
              whileHover={{ scale: 1.02 }}
              className="relative group cursor-pointer rounded-xl overflow-hidden aspect-square"
              onClick={() => setSelectedPhoto(photo)}
              data-testid={`gallery-photo-${photo.id}`}
            >
              <img 
                src={photo.image_url} 
                alt={photo.description || 'Service photo'}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center">
                <Eye className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-all" />
              </div>
              {photo.photo_type && (
                <span className={`absolute top-2 left-2 text-xs px-2 py-0.5 rounded-full ${
                  photo.photo_type === 'after' ? 'bg-green-500 text-white' : 'bg-amber-500 text-white'
                }`}>
                  {photo.photo_type === 'after' ? 'After' : 'Before'}
                </span>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold font-['Cormorant_Garamond'] flex items-center gap-2">
            <Image className="w-6 h-6 text-[var(--primary)]" />
            Service Gallery
          </h2>
          <p className="text-[var(--text-muted)] text-sm mt-1">
            Photos from completed services
          </p>
        </div>
      </div>

      {myPhotos.length === 0 && showcasePhotos.length === 0 ? (
        <div className="card p-12 text-center">
          <Camera className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
          <h3 className="text-lg font-bold mb-2">No Photos Yet</h3>
          <p className="text-[var(--text-secondary)] mb-4">
            After your services are completed, workers may upload before/after photos with your approval.
          </p>
          <p className="text-sm text-[var(--text-muted)]">
            Photos help us maintain quality and showcase our work!
          </p>
        </div>
      ) : (
        <>
          {myPhotos.length > 0 && (
            <PhotoGrid 
              photos={myPhotos} 
              title="My Service Photos"
              emptyMessage="No photos from your services yet"
            />
          )}
          
          {showcasePhotos.length > 0 && (
            <PhotoGrid 
              photos={showcasePhotos} 
              title="Showcase Gallery"
              emptyMessage="No showcase photos available"
            />
          )}
        </>
      )}

      {/* Photo Modal */}
      {selectedPhoto && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-4xl max-h-[90vh] relative"
            onClick={(e) => e.stopPropagation()}
          >
            <img 
              src={selectedPhoto.image_url} 
              alt={selectedPhoto.description || 'Service photo'}
              className="max-w-full max-h-[80vh] rounded-xl object-contain"
            />
            <div className="bg-[var(--background-paper)] rounded-lg p-4 mt-4">
              {selectedPhoto.description && (
                <p className="text-[var(--text-secondary)]">{selectedPhoto.description}</p>
              )}
              <div className="flex items-center gap-4 mt-2 text-sm text-[var(--text-muted)]">
                {selectedPhoto.service_name && (
                  <span>Service: {selectedPhoto.service_name}</span>
                )}
                {selectedPhoto.created_at && (
                  <span>{new Date(selectedPhoto.created_at).toLocaleDateString()}</span>
                )}
              </div>
            </div>
            <button 
              className="absolute top-2 right-2 w-8 h-8 bg-[var(--background-paper)] rounded-full flex items-center justify-center text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
              onClick={() => setSelectedPhoto(null)}
            >
              ✕
            </button>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ServiceGalleryTab;
